import React, { useState } from 'react';
import { Lock, CheckCircle, AlertCircle, Loader2, Eye, EyeOff, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';

const FirstLoginResetPasswordPage: React.FC = () => {
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const navigate = useNavigate();

  // Validações da senha
  const validacoes = {
    minimo8: novaSenha.length >= 8,
    temMaiuscula: /[A-Z]/.test(novaSenha),
    temMinuscula: /[a-z]/.test(novaSenha),
    temNumero: /\d/.test(novaSenha),
    temEspecial: /[!@#$%^&*(),.?":{}|<>]/.test(novaSenha),
    senhasIguais: novaSenha === confirmarSenha && novaSenha.length > 0
  };

  const senhaValida = Object.values(validacoes).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!senhaValida) {
      setErro('Por favor, atenda a todos os requisitos de senha');
      return;
    }

    setLoading(true);
    setErro(null);

    try {
      // 1. Atualizar a senha do usuário
      const { error: updateError } = await supabase.auth.updateUser({
        password: novaSenha
      });

      if (updateError) {
        throw updateError;
      }

      // 2. Obter ID do usuário logado
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // 3. Atualizar flags na tabela cp_usuarios
        const { error: cpError } = await supabase
          .from('cp_usuarios')
          .update({
            primeiro_acesso: false,
            senha_temporaria: false,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (cpError) {
          console.error('Erro ao atualizar cp_usuarios:', cpError);
          // Não bloquear o fluxo
        } else {
          console.log('✅ Flags atualizadas em cp_usuarios');
        }

        // 4. Remover flag de force_password_reset do user_metadata
        const { error: metadataError } = await supabase.auth.updateUser({
          data: {
            force_password_reset: false,
            first_login: false
          }
        });

        if (metadataError) {
          console.error('Erro ao atualizar metadata:', metadataError);
          // Não bloquear o fluxo
        }
      }

      // 5. Fazer logout e redirecionar para login com mensagem de sucesso
      await supabase.auth.signOut();
      
      // Redirecionar para login após 1 segundo
      setTimeout(() => {
        navigate('/', { state: { message: 'Senha alterada com sucesso! Faça login com sua nova senha.' } });
      }, 1000);

    } catch (error: any) {
      console.error('Erro ao alterar senha:', error);
      setErro(error.message || 'Erro ao alterar senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Bem-vindo ao CIPA Fácil!
            </h1>
            <p className="text-gray-600">
              Por segurança, você precisa criar uma nova senha no seu primeiro acesso.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo Nova Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nova Senha
              </label>
              <div className="relative">
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  className="w-full px-4 py-3 pl-11 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite sua nova senha"
                  required
                />
                <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                >
                  {mostrarSenha ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Campo Confirmar Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Senha
              </label>
              <div className="relative">
                <input
                  type={mostrarConfirmar ? 'text' : 'password'}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  className="w-full px-4 py-3 pl-11 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Confirme sua nova senha"
                  required
                />
                <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                >
                  {mostrarConfirmar ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Requisitos de Senha */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-3">Requisitos da senha:</p>
              <div className="space-y-2">
                <div className={`flex items-center gap-2 text-sm ${validacoes.minimo8 ? 'text-green-600' : 'text-gray-500'}`}>
                  <CheckCircle className={`w-4 h-4 ${validacoes.minimo8 ? 'opacity-100' : 'opacity-30'}`} />
                  <span>Mínimo de 8 caracteres</span>
                </div>
                <div className={`flex items-center gap-2 text-sm ${validacoes.temMaiuscula ? 'text-green-600' : 'text-gray-500'}`}>
                  <CheckCircle className={`w-4 h-4 ${validacoes.temMaiuscula ? 'opacity-100' : 'opacity-30'}`} />
                  <span>Pelo menos uma letra maiúscula</span>
                </div>
                <div className={`flex items-center gap-2 text-sm ${validacoes.temMinuscula ? 'text-green-600' : 'text-gray-500'}`}>
                  <CheckCircle className={`w-4 h-4 ${validacoes.temMinuscula ? 'opacity-100' : 'opacity-30'}`} />
                  <span>Pelo menos uma letra minúscula</span>
                </div>
                <div className={`flex items-center gap-2 text-sm ${validacoes.temNumero ? 'text-green-600' : 'text-gray-500'}`}>
                  <CheckCircle className={`w-4 h-4 ${validacoes.temNumero ? 'opacity-100' : 'opacity-30'}`} />
                  <span>Pelo menos um número</span>
                </div>
                <div className={`flex items-center gap-2 text-sm ${validacoes.temEspecial ? 'text-green-600' : 'text-gray-500'}`}>
                  <CheckCircle className={`w-4 h-4 ${validacoes.temEspecial ? 'opacity-100' : 'opacity-30'}`} />
                  <span>Pelo menos um caractere especial (!@#$%...)</span>
                </div>
                <div className={`flex items-center gap-2 text-sm ${validacoes.senhasIguais ? 'text-green-600' : 'text-gray-500'}`}>
                  <CheckCircle className={`w-4 h-4 ${validacoes.senhasIguais ? 'opacity-100' : 'opacity-30'}`} />
                  <span>As senhas coincidem</span>
                </div>
              </div>
            </div>

            {/* Mensagem de Erro */}
            {erro && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{erro}</p>
              </div>
            )}

            {/* Botão Confirmar */}
            <button
              type="submit"
              disabled={!senhaValida || loading}
              className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 flex items-center justify-center gap-2
                ${senhaValida && !loading 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl' 
                  : 'bg-gray-300 cursor-not-allowed'}`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Alterando senha...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Confirmar Nova Senha
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginResetPasswordPage;
